import { createElement, useEffect, useMemo, useState } from 'react';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { Text } from 'flow-builder/node/text';
import { useEditorContext } from '../../editor.context';
import { getIsNodeHighlighted } from '../../utils/node/getIsNodeHighlighted/getIsNodeHighlighted';
import { componentBaseRenderer } from '../utils/componentBaseRenderer';
import { ActionNode } from './actionNodeComponent';
import { useActionNodeTextures } from './actionNodeTextures';
import { useArrowTextures } from './nodeArrowTextures';
import { Node } from './nodeComponent';
import { useNodeComponentBaseTextures, useNodePortTextures, } from './nodeTextures';
export const fallbackNode = {
    label: 'Unknown',
    iconName: 'exclamation',
    color: 0x5644d8,
    translatePayload: () => [],
    updateConnection: (node) => node,
    NodeContent: function NodeContent() {
        return createElement(Text, {
            weight: 'normal',
            text: 'Unknown node type',
        });
    },
};
export const useNodeRenderer = ({ containers, layers, }) => {
    const { nodeContainer } = containers;
    const [nodes, setNodes] = useState({});
    const [actionNodes, setAttachNodes] = useState({});
    const editorContext = useEditorContext();
    const nodePortTextures = useNodePortTextures();
    const actionNodeTextureMap = useActionNodeTextures();
    const nodeComponentBaseTextureMap = useNodeComponentBaseTextures();
    const arrowTextures = useArrowTextures();
    const { props: { flow }, state: { nodeState: { relations }, }, handlers: { node: { onUpdateHeight }, }, view: { nodes: relayedNodesRef }, } = editorContext;
    const groupedRelations = useMemo(() => Object.entries(relations).reduce((groupedRelations, [key, node]) => {
        const isActionNode = node.nodeType === FlowNodeTypes.ACTION;
        groupedRelations[isActionNode ? 'actionNodes' : 'nodes'][key] = node;
        return groupedRelations;
    }, { nodes: {}, actionNodes: {} }), [relations]);
    useEffect(() => {
        relayedNodesRef.current = Object.assign(Object.assign({}, nodes), actionNodes);
    }, [actionNodes, nodes, relayedNodesRef]);
    /*
     * Render nodes & handle updates.
     */
    useEffect(() => {
        if (!nodeContainer ||
            !nodeComponentBaseTextureMap ||
            !actionNodeTextureMap ||
            !arrowTextures ||
            !nodePortTextures) {
            return;
        }
        const { state: { highlightState: highlight }, } = editorContext;
        const updateNodeHeightReferences = (items) => {
            const heightById = Object.entries(items).reduce((acc, [id, item]) => {
                const { height: stateItemHeight } = relations[id];
                if (stateItemHeight !== item.height) {
                    acc[id] = item.height;
                }
                return acc;
            }, {});
            if (Object.keys(heightById).length > 0) {
                onUpdateHeight(heightById);
            }
        };
        const addItemsToContainer = (items) => {
            relayedNodesRef.current = Object.assign(Object.assign({}, relayedNodesRef.current), items);
            Object.values(items).forEach((item) => {
                nodeContainer.addChild(item.ref);
            });
        };
        const onDestroyItems = (ids) => {
            ids.forEach((id) => {
                const component = nodes[id] || actionNodes[id];
                component.unmount();
            });
        };
        const staticCommonProps = {
            editorContext,
            layers,
        };
        /*
         * Action node renderer
         */
        componentBaseRenderer({
            source: groupedRelations.actionNodes,
            state: actionNodes,
            onGetProps: (node) => {
                const { id } = node;
                const isHighlighted = getIsNodeHighlighted(id, highlight.nodes);
                return Object.assign(Object.assign({}, staticCommonProps), { node: Object.assign({}, node), actionNodeTextureMap, isAttachNode: true, isHighlighted });
            },
            onGetItem: (props) => new ActionNode(props),
            onDestroyItems,
            onAddItems: addItemsToContainer,
            onUpdateState: setAttachNodes,
        });
        /*
         * Base node renderer
         */
        componentBaseRenderer({
            source: groupedRelations.nodes,
            state: nodes,
            onGetProps: (node) => {
                const { id, nodeType } = node;
                const attributes = flow.attributes[nodeType] || fallbackNode;
                const isHighlighted = getIsNodeHighlighted(id, highlight.nodes);
                const nodeComponentBase = nodeComponentBaseTextureMap[nodeType] ||
                    nodeComponentBaseTextureMap.fallback;
                return Object.assign(Object.assign({}, staticCommonProps), { node: Object.assign({}, node), nodeAttributes: attributes, nodeComponentBase,
                    nodePortTextures,
                    arrowTextures,
                    isHighlighted });
            },
            onGetItem: (props) => new Node(props),
            onDestroyItems,
            onItemRedraw: updateNodeHeightReferences,
            onAddItems: (items) => {
                updateNodeHeightReferences(items);
                addItemsToContainer(items);
            },
            onUpdateState: setNodes,
        });
    }, [
        nodeContainer,
        layers,
        arrowTextures,
        nodePortTextures,
        nodeComponentBaseTextureMap,
        actionNodeTextureMap,
        onUpdateHeight,
        relayedNodesRef,
        flow.attributes,
        groupedRelations,
        nodes,
        actionNodes,
        editorContext,
        relations,
        setNodes,
        setAttachNodes,
    ]);
};
